import { profileApi } from 'entities/profile/api/profileApi';
import { makeAutoObservable, runInAction } from 'mobx';
import { Role } from 'app/store/lib/authStore';
import avatarPlaceholder from 'shared/assets/images/avatarPlaceholder.png';

export type ProfileData = {
  avatar: string | null;
  id: string;
  email: string;
  name: string;
  surname: string;
  role: Role;
};

export type EditProfileData = {
  email: string;
  name: string;
  surname: string;
};

export type EditProfileAvatarData = {
  avatar: File | null;
};

export class ProfileModel {
  private _data: ProfileData;
  public isPending = false;
  public isError = false;

  constructor(data: ProfileData) {
    this._data = data;
    makeAutoObservable(this);
  }

  public get id() {
    return this._data.id;
  }

  public get email() {
    return this._data.email;
  }

  public get name() {
    return this._data.name;
  }

  public get surname() {
    return this._data.surname;
  }

  public get avatar() {
    return this._data.avatar || '';
  }

  public get avatarPlaceholder() {
    return avatarPlaceholder;
  }

  private get _role() {
    return this._data.role;
  }

  public get isAdmin() {
    return this._role === Role.ADMIN;
  }

  public get isOnlyView() {
    return this._role === Role.VIEWER;
  }

  public async update(params: EditProfileData) {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      const data = await profileApi.updateMe(params);

      runInAction(() => {
        this._data = data;
        this.isPending = false;
      });

      return data;
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      throw e;
    }
  }

  public async updateAvatar(params: EditProfileAvatarData) {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      const data = await profileApi.updateMeAvatar(params);

      runInAction(() => {
        this._data = data;
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      throw e;
    }
  }

  public async deleteAvatar() {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      await profileApi.deleteMeAvatar();

      runInAction(() => {
        this._data.avatar = null;
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      throw e;
    }
  }
}
